import deepFreeze from "deep-freeze";

import { AI_WORKBENCH_DOMAIN, LIONBRIDGE_CMP_URL, SNAKE_URL, WALRUS_URL } from "./constants";
import { AssessmentId, ProjectId, RevisionBatchId } from "./types";

// Maximum possible URL length, above which it might not be supported by browsers
// https://stackoverflow.com/questions/417142/what-is-the-maximum-length-of-a-url-in-different-browsers#417184
const MAX_URL_LENGTH = 4000;

export const JOBS = deepFreeze({
  LIST: "/jobs",
});

export const PROJECTS = deepFreeze({
  LIST: "/projects",
  CREATE: "/projects/create",
  CLONE: "/projects/create?clone=:projectId",
  DETAILS: "/projects/:projectId",
  PROGRESS: "/projects/:projectId/progress",
  CONTRIBUTORS: "/projects/:projectId/contributors",
  DOWNLOAD_ITEM_MEDIA_OUTPUT: "/projects/:projectId/items/:itemId/outputs/:outputIndex/media",
});

export const REVISIONS = deepFreeze({
  BATCH_LIST: "/projects/:projectId/revision",
  BATCH_DETAILS: "/projects/:projectId/revision/:batchId",
});

export const SIGNUP = deepFreeze({
  ORGANIZATION: "/signup",
});

export const USER_FLOW = deepFreeze({
  SIGN_IN: "/signin",
  FORGOT_PASSWORD: "/forgot_password",
  RESET_PASSWORD: "/reset_password",
});

export const ACCOUNT = deepFreeze({
  USER_PROFILE: "/account/settings",
  CHANGE_PASSWORD: "/account/change_password",
});

export const USERS = deepFreeze({ LIST: "/users", DETAILS: "/users/:userId" });

export const ORGANIZATIONS = deepFreeze({
  LIST: "/organizations",
  DETAILS: "/organizations/:organizationId",
});

export const ASSESSMENT = deepFreeze({
  LIST: "/tests",
  CREATE: "/tests/create",
  CLONE: "/tests/create?clone=:assessmentId",
  DETAILS: "/tests/:assessmentId",
  CONTRIBUTORS: "/tests/:assessmentId/contributors",
  PROGRESS: "/tests/:assessmentId/progress",
  TEST_QUESTIONS: "/tests/:assessmentId/test_questions",
});

export const GROUP_ASSESSMENT = deepFreeze({
  LIST: "/tests/groups",
  CREATE: "/tests/groups/create",
  CONTRIBUTORS: "/tests/groups/:groupAssessmentId/contributors",
  DETAILS: "/tests/groups/:groupAssessmentId",
});

export const SUPPORT = deepFreeze({ FAQ: "/support" });

export const LEGAL = deepFreeze({
  ACCEPT_AGREEMENTS: "/legal/agreements",
});

export function generateWorkbenchPath(projectId: ProjectId): string {
  const workbenchURL = new URL(
    `${AI_WORKBENCH_DOMAIN}/categorization/projects/${projectId}/workbench`
  );
  return workbenchURL.toString();
}

export function generateWorkbenchPreviewPath(projectId: ProjectId): string {
  const workbenchURL = new URL(
    `${AI_WORKBENCH_DOMAIN}/categorization/projects/${projectId}/preview`
  );
  return workbenchURL.toString();
}

export function generateWorkbenchRevisionBatchPath(batchId: RevisionBatchId): string {
  const workbenchURL = new URL(
    `${AI_WORKBENCH_DOMAIN}/categorization/revisions/batches/${batchId}/workbench`
  );
  return workbenchURL.toString();
}

export function generateWorkbenchAssessmentPath(assessmentId: AssessmentId): string {
  const workbenchURL = new URL(
    `${AI_WORKBENCH_DOMAIN}/categorization/tests/${assessmentId}/workbench`
  );
  return workbenchURL.toString();
}

export function generateWorkbenchGroupAssessmentPath(assessmentId: AssessmentId): string {
  const workbenchURL = new URL(
    `${AI_WORKBENCH_DOMAIN}/categorization/tests/groups/${assessmentId}/workbench`
  );
  return workbenchURL.toString();
}

export function generateWorkbenchAssessmentPreviewPath(assessmentId: AssessmentId): string {
  const workbenchURL = new URL(
    `${AI_WORKBENCH_DOMAIN}/categorization/tests/${assessmentId}/preview`
  );
  return workbenchURL.toString();
}

export function generateWorkbenchUnsavedPreviewPath(payload: string): string | null {
  const workbenchURL = new URL(`${AI_WORKBENCH_DOMAIN}/categorization/projects/preview-new`);
  const url = `${workbenchURL.toString()}?payload=${encodeURIComponent(payload)}`;
  // Temporary fix for https://telusinternational-ti.atlassian.net/browse/TIAIPRODUCT-5857
  if (url.length > MAX_URL_LENGTH) {
    return null;
  }
  return url;
}

export const LIONBRIDGE_CMP = deepFreeze({
  HOME: `${LIONBRIDGE_CMP_URL}/contributor/dashboard`,
  FIND_JOBS: `${LIONBRIDGE_CMP_URL}/contributor/jobs/available`,
  MY_WORK_ACTIVE_PROJECTS: `${LIONBRIDGE_CMP_URL}/contributor/my-work/active-projects`,
  LOGOUT: `${LIONBRIDGE_CMP_URL}/logout`,
  SUPPORT: `${LIONBRIDGE_CMP_URL}/contributor/support`,
  USER_PROFILE: `${LIONBRIDGE_CMP_URL}/contributor/userprofile/basic-info`,
});

export const SNAKE = deepFreeze({
  HOME: `${SNAKE_URL}`,
  LOGOUT: `${SNAKE_URL}/logout`,
  RESET_PASSWORD: `${SNAKE_URL}/reset_password?password_reset_token=`,
  RESET_PASSWORD_PAGE: `${SNAKE_URL}/reset_password`,
  FORGOT_PASSWORD: `${SNAKE_URL}/?trigger_reset_modal=opened`,
  BIOMETRIC_AUTH: `${SNAKE_URL}/biometric_authentication`,
});

export const WALRUS = deepFreeze({
  WORKSHEETS: `${WALRUS_URL}/worksheets`,
});
